import React, { useMemo } from 'react';
import { TrendingUp, Zap } from 'lucide-react';
import RangeInput from './RangeInput';

const ProjectionsView = ({
  initialCapital, setInitialCapital,
  monthlyContribution, setMonthlyContribution,
  expectedReturn, setExpectedReturn,
  horizon, setHorizon,
  formatCurrency
}) => {

  const projectedValues = useMemo(() => {
    let current = initialCapital;
    const monthlyRate = expectedReturn / 100 / 12;
    const points = [];
    for (let i = 0; i <= horizon * 12; i++) {
      points.push(current);
      current = current * (1 + monthlyRate) + monthlyContribution;
    }
    return points;
  }, [initialCapital, monthlyContribution, expectedReturn, horizon]);

  const finalValue = projectedValues[projectedValues.length - 1] || 0;
  const maxValue = Math.max(...projectedValues, initialCapital);

  // Ensure we have valid data
  const hasValidData = projectedValues.length > 0 && maxValue > 0;

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-[#041627] mb-2 font-headline">Simulador de Escenarios</h1>
        <p className="text-sm text-slate-500">Visualiza el crecimiento de tu patrimonio según tus aportes mensuales y tasas de retorno.</p>
      </div>

      <div className="grid grid-cols-12 gap-8">
        <div className="col-span-12 lg:col-span-4 space-y-6">
          <div className="bg-white p-5 md:p-8 rounded-xl shadow-sm border border-slate-100 space-y-6">
            <h4 className="font-bold text-lg mb-4">Simulation Parameters</h4>
            <RangeInput label="Initial Capital (CLP)" val={initialCapital} setVal={setInitialCapital} min={1000000} max={100000000} step={1000000} />
            <RangeInput label="Monthly Contribution (CLP)" val={monthlyContribution} setVal={setMonthlyContribution} min={0} max={5000000} step={50000} />
            <RangeInput label="Annual Return (%)" val={expectedReturn} setVal={setExpectedReturn} min={1} max={25} step={0.5} />
            <RangeInput label="Horizon (Years)" val={horizon} setVal={setHorizon} min={1} max={30} step={1} />
          </div>

          <div className="bg-[#041627] text-white p-5 md:p-8 rounded-xl shadow-lg">
            <p className="text-[10px] font-bold uppercase opacity-60 mb-2">End of Period Equity</p>
            <h3 className="text-3xl font-bold mb-2">{(finalValue / 1000000).toFixed(1)}M <span className="text-sm opacity-50 font-normal">CLP</span></h3>
            <p className="text-xs text-[#6bfe9c] font-bold flex items-center gap-1">
              <TrendingUp size={14} /> +{((finalValue/initialCapital - 1) * 100).toFixed(1)}% Return
            </p>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-8 bg-white p-5 md:p-8 rounded-xl shadow-sm border border-slate-100 flex flex-col">
          <div className="flex justify-between items-center mb-10">
            <h4 className="font-bold text-xl text-[#041627]">Growth Path Projection</h4>
            <div className="flex gap-4 text-xs font-bold text-slate-400">
               <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-[#041627]"></div> My Projection</span>
            </div>
          </div>
          <div className="flex-1 min-h-[300px] flex items-end justify-between gap-1 border-l border-b border-slate-100 pl-4 pb-4">
             {/* Dynamic Bars for projection */}
             {hasValidData ? (
               Array.from({ length: horizon + 1 }, (_, yearIndex) => {
                 const monthIndex = yearIndex * 12;
                 const val = projectedValues[monthIndex] || 0;

                 // Simple height calculation
                 const height = maxValue > 0 ? Math.max((val / maxValue) * 100, 5) : 5;

                 return (
                   <div key={`year-${yearIndex}`} className="flex-1 flex flex-col items-center group relative max-w-[40px]">
                     <div
                       className="w-full bg-[#041627] rounded-t-sm transition-all duration-300 group-hover:bg-[#6bfe9c]"
                       style={{ height: `${height}%` }}
                       title={`Year ${yearIndex}: ${formatCurrency(val)}`}
                     ></div>
                     <span className="absolute -bottom-6 text-[8px] font-bold text-slate-400">Y{yearIndex}</span>
                   </div>
                 );
               })
             ) : (
               <div className="w-full h-full flex items-center justify-center text-slate-400">
                 <p>Adjust parameters to see projection</p>
               </div>
             )}
          </div>
          <div className="mt-12 bg-[#f2f4f7] p-4 rounded-lg flex items-center gap-4">
            <div className="p-2 bg-white rounded shadow-sm"><Zap size={20} className="text-[#e9c349]" /></div>
            <p className="text-xs text-slate-600">Aumentar tu ahorro un 10% aceleraría tu objetivo en <b>7 meses</b>.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectionsView;